import {
  cerebras,
  DEFAULT_CEREBRAS_BASE_URL,
} from "../../connectors/cerebras/manifest";
import {
  DEFAULT_DEEPSEEK_BASE_URL,
  deepseek,
} from "../../connectors/deepseek/manifest";
import {
  DEFAULT_FIREWORKS_BASE_URL,
  fireworksAi,
} from "../../connectors/fireworksAi/manifest";
import { DEFAULT_GROK_BASE_URL, grok } from "../../connectors/grok/manifest";
import { DEFAULT_GROQ_BASE_URL, groq } from "../../connectors/groq/manifest";
import { liteLlm } from "../../connectors/liteLlm/manifest";
import {
  DEFAULT_MISTRAL_BASE_URL,
  mistralAi,
} from "../../connectors/mistralAi/manifest";
import { openaiCompatible } from "../../connectors/openaiCompatible/manifest";
import {
  DEFAULT_OPENROUTER_BASE_URL,
  openRouter,
} from "../../connectors/openRouter/manifest";
import {
  DEFAULT_PERPLEXITY_BASE_URL,
  perplexity,
} from "../../connectors/perplexity/manifest";
import {
  DEFAULT_TOGETHER_BASE_URL,
  togetherAi,
} from "../../connectors/togetherAi/manifest";
import type { ConnectorType } from "../../connectors/types";
import { defineAIProviderManifest } from "../manifest";
import { OPENAI_COMPATIBLE_CONTEXT_LIMIT_PATTERNS } from "./manifest";

// A connector belongs here only if the proxy meters its tokens — it must declare
// `usage` and appear in `family()` in services/proxy/src/metering/extract/mod.rs,
// or the provider's spend limits silently never fire.
function connectorProvider<const Id extends string>({
  connector,
  defaultBaseUrl,
  description = `Connect to ${connector.name} through its OpenAI-compatible API.`,
  exampleChatModel,
  exampleFastModel,
  documentsReasoningContent,
}: {
  connector: ConnectorType<Id>;
  // Omitted for gateways with no vendor endpoint of their own.
  defaultBaseUrl?: string;
  description?: string;
  exampleChatModel: string;
  exampleFastModel: string;
  // Groq, Cerebras, and Mistral answer 400/422 when an assistant message carries
  // `reasoning_content`, so replaying thinking is opt-in per vendor.
  documentsReasoningContent?: boolean;
}) {
  return defineAIProviderManifest({
    id: connector.identifier,
    label: connector.name,
    description,
    ...(defaultBaseUrl ? { defaultBaseUrl } : {}),
    connectorIdentifier: connector.identifier,
    endpointFromConnector: true,
    defaultChatModel: exampleChatModel,
    defaultFastModel: exampleFastModel,
    configurable: true,
    requiresChatModel: true,
    contextLimitPatterns: OPENAI_COMPATIBLE_CONTEXT_LIMIT_PATTERNS,
    ...(documentsReasoningContent
      ? { reasoningReplay: { via: "text" as const } }
      : {}),
    credentialFields: [
      {
        key: "apiKey",
        label: "API key",
        description: "Supplied by the connector.",
        placeholder: "",
        sensitive: true,
        required: true,
      },
    ],
    packCredentials: (v) => ({ baseUrl: v.baseUrl?.trim() ?? "", apiKey: "" }),
    unpackCredentials: (row) => ({ baseUrl: row.baseUrl, apiKey: "" }),
    subtitleField: "baseUrl",
    attachmentSupport: { maxImageDimension: 2000 },
  });
}

export const openAICompatibleConnectorProviders = [
  connectorProvider({
    connector: cerebras,
    defaultBaseUrl: DEFAULT_CEREBRAS_BASE_URL,
    exampleChatModel: "gpt-oss-120b",
    exampleFastModel: "llama3.1-8b",
  }),
  connectorProvider({
    connector: deepseek,
    defaultBaseUrl: DEFAULT_DEEPSEEK_BASE_URL,
    exampleChatModel: "deepseek-chat",
    exampleFastModel: "deepseek-chat",
    documentsReasoningContent: true,
  }),
  connectorProvider({
    connector: fireworksAi,
    defaultBaseUrl: DEFAULT_FIREWORKS_BASE_URL,
    exampleChatModel: "accounts/fireworks/models/kimi-k2-instruct",
    exampleFastModel: "accounts/fireworks/models/llama-v3p1-8b-instruct",
    documentsReasoningContent: true,
  }),
  connectorProvider({
    connector: grok,
    defaultBaseUrl: DEFAULT_GROK_BASE_URL,
    exampleChatModel: "grok-4",
    exampleFastModel: "grok-4-fast",
  }),
  connectorProvider({
    connector: groq,
    defaultBaseUrl: DEFAULT_GROQ_BASE_URL,
    exampleChatModel: "openai/gpt-oss-120b",
    exampleFastModel: "llama-3.3-70b-versatile",
  }),
  connectorProvider({
    connector: liteLlm,
    exampleChatModel: "gpt-5",
    exampleFastModel: "gpt-5-mini",
    documentsReasoningContent: true,
  }),
  connectorProvider({
    connector: mistralAi,
    defaultBaseUrl: DEFAULT_MISTRAL_BASE_URL,
    exampleChatModel: "mistral-large-latest",
    exampleFastModel: "mistral-small-latest",
  }),
  connectorProvider({
    connector: openRouter,
    defaultBaseUrl: DEFAULT_OPENROUTER_BASE_URL,
    exampleChatModel: "anthropic/claude-opus-4-8",
    exampleFastModel: "anthropic/claude-haiku-4-5",
    documentsReasoningContent: true,
  }),
  connectorProvider({
    connector: perplexity,
    defaultBaseUrl: DEFAULT_PERPLEXITY_BASE_URL,
    exampleChatModel: "sonar-pro",
    exampleFastModel: "sonar",
  }),
  connectorProvider({
    connector: togetherAi,
    defaultBaseUrl: DEFAULT_TOGETHER_BASE_URL,
    exampleChatModel: "deepseek-ai/DeepSeek-V3",
    exampleFastModel: "meta-llama/Llama-3.3-70B-Instruct-Turbo",
    documentsReasoningContent: true,
  }),
  connectorProvider({
    connector: openaiCompatible,
    description: "Connect to any OpenAI-compatible API endpoint.",
    exampleChatModel: "chat-model",
    exampleFastModel: "fast-model",
    documentsReasoningContent: true,
  }),
] as const;

export type OpenAICompatibleConnectorProviderId =
  (typeof openAICompatibleConnectorProviders)[number]["id"];

const connectorsByProviderId = {
  cerebras,
  deepseek,
  fireworksAi,
  grok,
  groq,
  liteLlm,
  mistralAi,
  openRouter,
  perplexity,
  togetherAi,
  openaiCompatible,
} satisfies Record<OpenAICompatibleConnectorProviderId, ConnectorType>;

export const openAICompatibleConnectorProviderLogos = Object.fromEntries(
  Object.entries(connectorsByProviderId).map(([id, connector]) => [
    id,
    connector.logo,
  ])
) as Record<OpenAICompatibleConnectorProviderId, string>;

export function connectorForOpenAICompatibleProvider(
  id: OpenAICompatibleConnectorProviderId
): ConnectorType {
  return connectorsByProviderId[id];
}
